import { auditActionLabel, type AuditEntry, type Paginated } from '@ftthnms/shared';
import { Badge, Button, EmptyState } from '@ftthnms/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, History, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Pager, RequireAbility } from '../components/common';
import { Drawer } from '../components/Drawer';
import { useToast } from '../components/Toast';
import { api, dateTime, qs } from '../lib/api';
import { download } from '../lib/download';

interface Facets {
  users: Array<{ id: string; name: string }>;
  actions: Array<{ value: string; label: string }>;
  entities: string[];
}

const ENTITY_LABELS: Record<string, string> = {
  asset: 'Aset',
  route: 'Jalur',
  port: 'Port',
  area: 'Wilayah',
  user: 'Pengguna',
  sso_user: 'Akun SSO',
  sso_mapping: 'Pemetaan role',
  role: 'Peran',
  import: 'Import',
  assets: 'Export aset',
  routes: 'Export jalur',
  customers: 'Export pelanggan',
  kmz: 'Export KMZ',
  audit_log: 'Audit log',
  incident: 'Insiden',
  settings: 'Pengaturan',
  masterdata: 'Master data',
  pricing: 'Harga',
};

const TONE: Array<[RegExp, 'danger' | 'warning' | 'success' | 'primary']> = [
  [/denied|delete/, 'danger'],
  [/permissions|user\.update|settings|sso_mapping|pricing|masterdata/, 'warning'],
  [/create|restore|login/, 'success'],
  [/export|import/, 'primary'],
];

/** Audit log (AUD-01/02): who did what and when, filterable and exportable. Admin only. */
export function AuditPage() {
  return (
    <RequireAbility action="read" subject="AuditLog">
      <AuditLog />
    </RequireAbility>
  );
}

function AuditLog() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const [exporting, setExporting] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, userId, action, entity, from, to]);

  const filter = { q: debouncedQ, userId, action, entity, from, to };
  const facets = useQuery({
    queryKey: ['audit', 'facets'],
    queryFn: () => api<Facets>('/api/v1/audit/facets'),
    staleTime: 60_000,
  });
  const log = useQuery({
    queryKey: ['audit', filter, page],
    queryFn: () => api<Paginated<AuditEntry>>(`/api/v1/audit${qs({ ...filter, page, pageSize })}`),
    placeholderData: keepPreviousData,
  });

  async function exportCsv() {
    setExporting(true);
    try {
      await download(`/api/v1/audit/export${qs(filter)}`, 'ftthnms-audit.csv');
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setExporting(false);
    }
  }

  const data = log.data;
  const filtered = !!(debouncedQ || userId || action || entity || from || to);
  return (
    <div className="fn-page-body">
      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-toolbar" style={{ flexWrap: 'wrap' }}>
          <div className="fn-input-group" style={{ flex: 1, maxWidth: 260, minWidth: 160 }}>
            <Search className="i" aria-hidden />
            <input
              className="fn-input"
              aria-label="Cari"
              placeholder="Cari kode aset, IP, isi perubahan…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="fn-input"
            aria-label="Pengguna"
            style={{ width: 'auto', maxWidth: 200 }}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Semua pengguna</option>
            {facets.data?.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          <select
            className="fn-input"
            aria-label="Aksi"
            style={{ width: 'auto', maxWidth: 200 }}
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="">Semua aksi</option>
            {facets.data?.actions.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          <select
            className="fn-input"
            aria-label="Entitas"
            style={{ width: 'auto' }}
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
          >
            <option value="">Semua entitas</option>
            {facets.data?.entities.map((e) => (
              <option key={e} value={e}>
                {ENTITY_LABELS[e] ?? e}
              </option>
            ))}
          </select>
          <input
            className="fn-input"
            type="date"
            aria-label="Dari tanggal"
            style={{ width: 'auto' }}
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="fn-hint">s/d</span>
          <input
            className="fn-input"
            type="date"
            aria-label="Sampai tanggal"
            style={{ width: 'auto' }}
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
          <span className="fn-toolbar__spacer" />
          <Button
            size="sm"
            icon={<Download className="i" aria-hidden />}
            loading={exporting}
            onClick={() => void exportCsv()}
          >
            Export CSV
          </Button>
        </div>

        {data && data.total === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState
              icon={<History className="i i--xl" aria-hidden />}
              title={filtered ? 'Tidak ada catatan yang cocok' : 'Belum ada aktivitas'}
            />
          </div>
        ) : (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Pengguna</th>
                  <th>Aksi</th>
                  <th>Entitas</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setOpen(e)}
                    style={{ cursor: 'pointer' }}
                    aria-selected={open?.id === e.id}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>{dateTime.format(new Date(e.at))}</td>
                    <td>{e.userName ?? <span className="fn-hint">—</span>}</td>
                    <td>
                      <Badge tone={TONE.find(([re]) => re.test(e.action))?.[1] ?? 'neutral'}>
                        {auditActionLabel(e.action)}
                      </Badge>
                    </td>
                    <td>
                      <EntityRef e={e} />
                    </td>
                    <td className="mono">{e.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={data?.total ?? 0} unit="catatan" page={page} pageSize={pageSize} onPage={setPage} />
      </section>
      {open && <AuditDrawer entry={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function EntityRef({ e }: { e: AuditEntry }) {
  const kind = e.entity ? (ENTITY_LABELS[e.entity] ?? e.entity) : null;
  if (!kind) return <span className="fn-hint">—</span>;
  const label = e.entityLabel ?? e.entityId;
  return (
    <span>
      <span className="fn-hint">{kind}</span>
      {label && (
        <>
          {' · '}
          {e.entity === 'asset' && e.entityLabel ? (
            <Link
              to="/map"
              search={{ focus: e.entityId ?? undefined }}
              className="fn-link"
              onClick={(ev) => ev.stopPropagation()}
            >
              {label}
            </Link>
          ) : e.entity === 'route' && e.entityLabel ? (
            <Link
              to="/map"
              search={{ route: e.entityId ?? undefined }}
              className="fn-link"
              onClick={(ev) => ev.stopPropagation()}
            >
              {label}
            </Link>
          ) : (
            <span className="mono">{label.length > 40 ? `${label.slice(0, 40)}…` : label}</span>
          )}
        </>
      )}
    </span>
  );
}

function AuditDrawer({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const rows = diffRows(entry.diff);
  return (
    <Drawer
      label="Detail audit"
      eyebrow="Audit log"
      title={auditActionLabel(entry.action)}
      subtitle={dateTime.format(new Date(entry.at))}
      onClose={onClose}
    >
      <div className="fn-drawer__section">
        <dl className="fn-dl">
          <dt>Pengguna</dt>
          <dd>{entry.userName ?? '—'}</dd>
          <dt>Entitas</dt>
          <dd>
            <EntityRef e={entry} />
          </dd>
          <dt>IP</dt>
          <dd className="mono">{entry.ip ?? '—'}</dd>
          <dt>Perangkat</dt>
          <dd style={{ fontSize: 12 }}>{entry.userAgent ?? '—'}</dd>
          <dt>Kode aksi</dt>
          <dd className="mono">{entry.action}</dd>
        </dl>
      </div>
      <div className="fn-drawer__section">
        <div className="fn-panel__label">Perubahan</div>
        {rows ? (
          rows.length === 0 ? (
            <p className="fn-hint">Tidak ada field yang berubah.</p>
          ) : (
            <table className="fn-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Sebelum</th>
                  <th>Sesudah</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([k, a, b]) => (
                  <tr key={k}>
                    <td className="mono">{k}</td>
                    <td style={{ color: 'var(--danger-text)', wordBreak: 'break-word' }}>{a}</td>
                    <td style={{ color: 'var(--success-text)', wordBreak: 'break-word' }}>{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : entry.diff === null || entry.diff === undefined ? (
          <p className="fn-hint">Tidak ada detail.</p>
        ) : (
          <pre
            className="mono"
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 8,
              background: 'var(--bg-surface-inset)',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(entry.diff, null, 2)}
          </pre>
        )}
      </div>
    </Drawer>
  );
}

/** `{before, after}` diffs become field rows (changed fields only); anything else is shown as JSON. */
function diffRows(diff: unknown): Array<[string, string, string]> | null {
  if (!diff || typeof diff !== 'object') return null;
  const d = diff as { before?: unknown; after?: unknown };
  if (!('before' in d) || !('after' in d)) return null;
  const before = flatten(d.before);
  const after = flatten(d.after);
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys.filter((k) => before[k] !== after[k]).map((k) => [k, before[k] ?? '—', after[k] ?? '—']);
}

function flatten(v: unknown, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [k, x] of Object.entries(v)) flatten(x, prefix ? `${prefix}.${k}` : k, out);
  } else if (prefix) {
    out[prefix] =
      v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
  return out;
}
